// HostelReports.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Grid, FormControl, Select, MenuItem, Button,
  TextField, Typography, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Fade, Alert, Snackbar
} from "@mui/material";

import axiosApi from "../Utils/axiosApi";
import HeaderMenu from "../Header/HeaderMenu";

// PDF/Excel libs (ensure installed in your project)
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

/**
 * Rewritten, cleaned, and optimized HostelReports component.
 * - Single fetchHostelers implementation (normalized hosteler data)
 * - Consistent state keys and filters
 * - Defensive payload building for APIs
 * - Memoized column list
 * - Better loading / error handling & snackbar
 * - Export: PDF (autoTable), Excel, CSV
 */

const apiList = {
  attendance: "/hostelerLeaveRequest/getHostelLeaveRequestReport",
  payment: "/hostelerPayment/getHostelerInvoiceReport",
  booking: "/hostelBooking/getBookingList",
  complaint: "/hostelerComplaint/getComplaintReport",
  scheduleCallback: "/hostelScheduleCallback/getReport",
  requestCallback: "/hostelRequestCallback/getReport",
  financeMonthLyreport: "/finance/monthlyReport",
};

const attendanceColumnOrder = [
  "hostelName",
  "hostelerName",
  "leaveStartDate",
  "leaveEndDate",
  "reason",
  "leaveRequestStatus",
  "leaveAppliedDate",
];
const paymentColumnOrder = [
  "hostelName",
  "hostelerName",
  "billingMonth",
  "totalBillAmount",
  "paidAmount",
  "paidDate",
  "paymentStatus",
  "advancePaymentAmount",
  "previousDueAmount",
  "closureHolidayFoodDeduction",
  "absentDateList",
  "leaveRequestFoodDeduction",
  "extraChargeTotalAmount",
  "ownerConcessionBillAmount",
  
];

const normalizeHostelsResponse = (res) => {
  // Accepts either [[id, name], ...] or [{...}] or other common shapes
  if (!res) return [];
  if (Array.isArray(res) && res.length > 0 && Array.isArray(res[0])) {
    // [[id, name], ...]
    return res.map(([id, name]) => ({ id: String(id), name: String(name) }));
  }
  if (Array.isArray(res)) {
    // array of objects (try to pick id/name fields)
    return res.map((h) => {
      const id = h.id ?? h.hostelId ?? h[0] ?? "";
      const name = h.name ?? h.hostelName ?? `${h.hostelName || ""}`.trim();
      return { id: String(id), name: String(name || "") };
    });
  }
  return [];
};

const normalizeHostelers = (data) => {
  // Accepts many shapes and returns objects with hostelerId and hostelerName
  if (!data) return [];
  if (!Array.isArray(data)) return [];

  return data.map((h) => {
    // possible shapes:
    // { hostelerId, hostelerName }
    // { id, hostelerName }
    // [id, name]
    // { hostlerFirstName, hostlerLastName, hostelerId }
    if (Array.isArray(h)) {
      const [id, name] = h;
      return { hostelerId: String(id ?? ""), hostelerName: String(name ?? "") };
    }
    const id = h.hostelerId ?? h.id ?? h.hostlerId ?? h[0] ?? "";
    const name =
      h.hostelerName ??
      h.hostlerName ??
      (h.hostlerFirstName || h.hostlerLastName
        ? `${h.hostlerFirstName ?? ""} ${h.hostlerLastName ?? ""}`.trim()
        : h.name ?? "");
    return { hostelerId: String(id), hostelerName: String(name ?? "") };
  });
};
const columnLabels = {
  hostelName: "Hostel",
  hostelerName: "Hosteler",
  billingMonth: "Month",
  totalBillAmount: "Total Bill",
  paidAmount: "Paid",
  paidDate: "Paid Date",
  paymentStatus: "Status",
  advancePaymentAmount: "Advance",
  previousDueAmount: "Previous Due",
  closureHolidayFoodDeduction: "Holiday Deduction",
  absentDateList: "Absent Days",
  leaveRequestFoodDeduction: "Leave Deduction",
  extraChargeTotalAmount: "Extra Charges",
  ownerConcessionBillAmount: "Concession",
};
const HostelReports = () => {
  // UI/data state
  const [reportType, setReportType] = useState("");
  const [reportData, setReportData] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [hostelers, setHostelers] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingHostelers, setLoadingHostelers] = useState(false);

  // Filters single source-of-truth
  const [filters, setFilters] = useState({
    hostelId: "",
    hostelerId: "",
    attendanceStatus: "",
    paymentStatus: "",
    fromDate: "",
    toDate: "",
    billMonth: "",
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  // Fetch hostels on mount
  useEffect(() => {
    let mounted = true;
    axiosApi
      .post("/hostel/getHostelName", {})
      .then((res) => {
        if (!mounted) return;
        const data = Array.isArray(res.data) ? res.data : res.data?.hostels ?? res.data;
        setHostels(normalizeHostelsResponse(data));
      })
      .catch((err) => {
        console.error("Failed to fetch hostels:", err);
        if (mounted) setHostels([]);
      });
    return () => (mounted = false);
  }, []);

  // Fetch hostelers when chosen hostel changes
  useEffect(() => {
    if (!filters.hostelId) {
      setHostelers([]);
      setFilters((f) => ({ ...f, hostelerId: "" }));
      return;
    }
    fetchHostelers(filters.hostelId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.hostelId]);

  // Single fetchHostelers function (normalized)
  const fetchHostelers = async (hostelId) => {
    if (!hostelId) {
      setHostelers([]);
      return;
    }
    try {
      setLoadingHostelers(true);
      const response = await axiosApi.post("/hosteler/getHosteler", {
        hostelIds: [Number(hostelId)],
      });
      const raw = Array.isArray(response.data) ? response.data : response.data?.hostelers ?? response.data;
      setHostelers(normalizeHostelers(raw));
    } catch (err) {
      console.error("Error fetching hostelers:", err);
      setHostelers([]);
      showMessage("Failed to fetch hostelers", "error");
    } finally {
      setLoadingHostelers(false);
    }
  };

  const showMessage = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  // Build payload defensively based on reportType
  const buildPayload = () => {
   const payloadCommon = {};
    switch (reportType) {
      case "attendance":
  return {
    ...payloadCommon,
    hostelIds: filters.hostelId ? [Number(filters.hostelId)] : [],
    hostelerIds: filters.hostelerId ? [Number(filters.hostelerId)] : [],
    leaveStartDate: filters.fromDate || null,
    leaveEndDate: filters.toDate || null,
    leaveRequestStatusList: filters.attendanceStatus
      ? [filters.attendanceStatus.toUpperCase()]
      : [],
  };
      case "payment":
  return {
    ...payloadCommon,
    hostelId: filters.hostelId ? Number(filters.hostelId) : null,
    hostelerIds: filters.hostelerId ? [Number(filters.hostelerId)] : [],
    paymentStatus: filters.paymentStatus || null,
    billingMonth: filters.billMonth || null,
  };

     case "complaint":
  return {
    ...payloadCommon,
    hostelIds: filters.hostelId ? [Number(filters.hostelId)] : [],
    hostelerIds: filters.hostelerId ? [Number(filters.hostelerId)] : [],
    fromDate: filters.fromDate || null,
    toDate: filters.toDate || null,
  };

     case "booking":
case "scheduleCallback":
case "requestCallback":
  return {
    ...payloadCommon,
    hostelIds: filters.hostelId ? [Number(filters.hostelId)] : [],
    fromDate: filters.fromDate || null,
    toDate: filters.toDate || null,
  };

     case "financeMonthLyreport":
  return {
    ...payloadCommon,
    hostelIds: filters.hostelId ? [Number(filters.hostelId)] : [],
    billMonth: filters.billMonth || null,
  };

      default:
        return payloadCommon;
    }
  };
const validateMandatoryDates = () => {
  // Skip date validation for Payment & Finance
  if (["financeMonthLyreport", "payment"].includes(reportType)) {
    return true;
  }

  if (!filters.fromDate) {
    showMessage("From Date is required", "warning");
    return false;
  }

  if (!filters.toDate) {
    showMessage("To Date is required", "warning");
    return false;
  }

  return true;
};

const validateDateRange = () => {
  if (!filters.fromDate || !filters.toDate) return true; // nothing to validate yet

  const from = new Date(filters.fromDate);
  const to = new Date(filters.toDate);

  if (to < from) {
    showMessage("To Date cannot be earlier than From Date", "warning");
    return false;
  }

  const diffDays = (to - from) / (1000 * 60 * 60 * 24);

  if (diffDays > 31) {
    showMessage("Date range cannot exceed 1 month (31 days)", "warning");
    return false;
  }

  return true;
};

  const generateReport = async () => {
    if (!reportType) return showMessage("Select Report Type", "warning");
    if (!filters.hostelId) return showMessage("Select Hostel", "warning");
	// Mandatory date validation
  if (!validateMandatoryDates()) return;

	// Validate 1 month limit for all reports except finance month
 if (!["financeMonthLyreport", "payment"].includes(reportType)) {
  if (!validateDateRange()) return;
}
    if (["financeMonthLyreport", "payment"].includes(reportType) && !filters.billMonth) {
  return showMessage("Select Bill Month", "warning");
}

    setLoading(true);
    setReportData([]);

    const payload = buildPayload();
    const apiPath = apiList[reportType];

    try {
      const res = await axiosApi.post(apiPath, payload);
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? res.data ?? [];
      if (!Array.isArray(data) || data.length === 0) {
        setReportData([]);
        showMessage("No records found", "info");
      } else {
        setReportData(data);
      }
    } catch (err) {
      console.error("API error:", err);
      console.error("Payload was:", payload);
      showMessage("Server Error - Check console for details", "error");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Memoized ordered columns
  const orderedColumns = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];
    if (reportType === "attendance") {
      return attendanceColumnOrder.filter((c) => c in reportData[0]);
    }
	if (reportType === "payment") {
      return paymentColumnOrder.filter((c) => c in reportData[0]);
    }
    return Object.keys(reportData[0]);
  }, [reportData, reportType]);

  // EXPORT helpers
  const exportPDF = () => {
    if (!reportData.length) return showMessage("No Data", "warning");
    try {
      const doc = new jsPDF();
      doc.text(`${reportType.toUpperCase()} REPORT`, 14, 12);
      autoTable(doc, {
        head: [orderedColumns.map((c) => c.toUpperCase())],
        body: reportData.map((row) => orderedColumns.map((col) => (row[col] == null ? "--" : String(row[col])))),
        startY: 18,
      });
      doc.save(`${reportType}_report.pdf`);
    } catch (err) {
      console.error("PDF export error:", err);
      showMessage("Failed to export PDF", "error");
    }
  };

  const exportExcel = () => {
    if (!reportData.length) return showMessage("No Data", "warning");
    try {
      const sheet = XLSX.utils.json_to_sheet(reportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet, "Report");
      XLSX.writeFile(wb, `${reportType}_report.xlsx`);
    } catch (err) {
      console.error("Excel export error:", err);
      showMessage("Failed to export Excel", "error");
    }
  };

  const exportCSV = () => {
    if (!reportData.length) return showMessage("No Data", "warning");
    try {
      const csv = XLSX.utils.sheet_to_csv(XLSX.utils.json_to_sheet(reportData));
      const blob = new Blob([csv], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${reportType}_report.csv`;
      link.click();
    } catch (err) {
      console.error("CSV export error:", err);
      showMessage("Failed to export CSV", "error");
    }
  };

  return (
    <>
      <HeaderMenu />
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight="700" textAlign="center" mb={3}>
          📊 Hostel Report Dashboard
        </Typography>

        <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0px 3px 10px rgba(0,0,0,0.1)" }}>
          <Grid container spacing={2} alignItems="center">
            {/* REPORT TYPE */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={reportType}
                  onChange={(e) => {
                    setReportType(e.target.value);
                    setReportData([]);
                    // reset filters which are irrelevant when report type changes
                    setFilters((f) => ({ ...f, hostelerId: "", attendanceStatus: "", paymentStatus: "", fromDate: "", toDate: "", billMonth: "" }));
                  }}
                >
                  <MenuItem value=""><em>Select Report</em></MenuItem>
                  <MenuItem value="attendance">Attendance Report</MenuItem>
                  <MenuItem value="payment">Payment Report</MenuItem>
                 
                </Select>
              </FormControl>
            </Grid>

            {/* HOSTEL */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={filters.hostelId}
                  onChange={(e) => setFilters((f) => ({ ...f, hostelId: e.target.value, hostelerId: "" }))}
                >
                  <MenuItem value=""><em>Select Hostel</em></MenuItem>
                  {hostels.map((h) => (
                    <MenuItem key={h.id} value={h.id}>
                      {h.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* HOSTELER */}
            {["attendance", "payment", "complaint"].includes(reportType) && (
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={filters.hostelerId}
                    onChange={(e) => setFilters((f) => ({ ...f, hostelerId: e.target.value }))}
                  >
                    <MenuItem value="">
                      <em>{loadingHostelers ? "Loading hostelers..." : "Select Hosteler"}</em>
                    </MenuItem>
                    {hostelers.map((h) => (
                      <MenuItem key={h.hostelerId} value={h.hostelerId}>
                        {h.hostelerName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* ATTENDANCE STATUS */}
            {reportType === "attendance" && (
  <Grid item xs={12} sm={3}>
    <FormControl fullWidth size="small">
      <Select
        displayEmpty
        value={filters.attendanceStatus}
        onChange={(e) =>
          setFilters((f) => ({ ...f, attendanceStatus: e.target.value }))
        }
      >
        <MenuItem value="">
          <em>Select Attendance Status</em>
        </MenuItem>

        <MenuItem value="PENDING">Pending</MenuItem>
        <MenuItem value="APPROVED">Approved</MenuItem>
      </Select>
    </FormControl>
  </Grid>
)}


            {/* PAYMENT STATUS */}
            {reportType === "payment" && (
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={filters.paymentStatus}
                    onChange={(e) => setFilters((f) => ({ ...f, paymentStatus: e.target.value }))}
                  >
                    <MenuItem value=""><em>Select Payment Status</em></MenuItem>
                    <MenuItem value="COMPLETED">Completed</MenuItem>
					<MenuItem value="PARTIALLY_COMPLETED">Partially Completed</MenuItem>
					<MenuItem value="PAYMENT_INITIATED">Payment Initiated</MenuItem>
                    <MenuItem value="NOT_STARTED">Not Started</MenuItem>
					 <MenuItem value="FAILED">Failed</MenuItem>
                    
					
					
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* BILL MONTH */}
            {["financeMonthLyreport", "payment"].includes(reportType) && (
              <Grid item xs={12} sm={3}>
               <TextField
  label="Billing Month"
  type="month"
  size="small"
  fullWidth
  value={filters.billMonth}
  onChange={(e) =>
    setFilters((f) => ({ ...f, billMonth: e.target.value }))
  }
  InputLabelProps={{ shrink: true }}
/>
              </Grid>
            )}

            {/* DATE RANGE */}
            {!["financeMonthLyreport", "payment"].includes(reportType) && (
              <>
                <Grid item xs={12} sm={2}>
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={filters.fromDate}
                    onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={filters.toDate}
                    onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                sx={{ height: "40px", fontWeight: "600" }}
                onClick={generateReport}
                disabled={loading}
              >
                Generate
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* TABLE / STATUS */}
        {loading && (
          <Box textAlign="center" mt={3}>
            <CircularProgress size={45} />
            <Typography mt={1}>Fetching Report...</Typography>
          </Box>
        )}

{!loading && reportData.length > 0 && (
  <Fade in>
    <Box>

      {/* MOBILE CARD VIEW */}
      <Box
        sx={{
          display: { xs: "block", sm: "none" },
          mt: 3,
        }}
      >
        {reportData.map((row, i) => (
          <Paper
            key={i}
            elevation={3}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              background: "#fff",
            }}
          >
            {orderedColumns.map((col) => (
              <Box
                key={col}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #eee",
                  py: 0.7,
                }}
              >
                <Typography sx={{ fontWeight: 700, color: "#444" }}>
                  {col.toUpperCase()}
                </Typography>
                <Typography sx={{ color: "#333" }}>
                  {row[col] == null || row[col] === "" ? "--" : String(row[col])}
                </Typography>
              </Box>
            ))}
          </Paper>
        ))}

        {/* EXPORT BUTTONS BELOW MOBILE CARDS */}
        <Box mt={3} display="flex" gap={2} flexWrap="wrap">
          <Button variant="contained" color="success" onClick={exportPDF}>PDF</Button>
          <Button variant="contained" onClick={exportExcel}>Excel</Button>
          <Button variant="contained" color="secondary" onClick={exportCSV}>CSV</Button>
        </Box>
      </Box>

      {/* DESKTOP TABLE VIEW */}

<Box sx={{ width: "100%", display: { xs: "none", sm: "block" } }}>

  {/* SCROLL WRAPPER — for table only */}
  <Box
    sx={{
      width: "100%",
      overflowX: "auto",
      overflowY: "hidden",
      pb: 1,
    }}
  >
    {/* Force wide content to trigger scroll */}
    <Box sx={{ width: "3000px" }}>
      <TableContainer
        component={Paper}
        sx={{
          mt: 3,
          borderRadius: 2,
          maxHeight: "70vh",
          overflowY: "auto",
          overflowX: "visible",
        }}
      >
        <Table stickyHeader sx={{ minWidth: 2200 }}>
          <TableHead>
            <TableRow>
              {orderedColumns.map((col) => (
               <TableCell
  key={col}
  sx={{
    fontWeight: "bold",
    background: "#1976d2",
    color: "#fff",
    whiteSpace: "nowrap",
    fontSize: "12px",        // 🔹 smaller text
    padding: "6px 8px",      // 🔹 reduce spacing
    maxWidth: 120,           // 🔹 limit width
    overflow: "hidden",
    textOverflow: "ellipsis" // 🔹 add "..." if long
  }}
>
                  {columnLabels[col] || col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {reportData.map((row, i) => (
              <TableRow key={i}>
                {orderedColumns.map((col) => (
                  <TableCell
  key={col}
  sx={{
    whiteSpace: "nowrap",
    fontSize: "12px",
    padding: "6px 8px",
    maxWidth: 120,
    overflow: "hidden",
    textOverflow: "ellipsis"
  }}
>
                    {row[col] == null ? "--" : String(row[col])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  </Box>

  {/* EXPORT BUTTONS — OUTSIDE scroll area */}
  <Box mt={3} display="flex" gap={2}>
    <Button variant="contained" color="success" onClick={exportPDF}>PDF</Button>
    <Button variant="contained" onClick={exportExcel}>Excel</Button>
    <Button variant="contained" color="secondary" onClick={exportCSV}>CSV</Button>
  </Box>

</Box>

    </Box>
  </Fade>
)}


      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ textAlign: "center", fontWeight: 600, fontSize: 15 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HostelReports;
